import React, { useRef } from 'react';
import { useIntl } from 'react-intl';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/dist/plugins/counter/index.js';
import Zoom from 'yet-another-react-lightbox/dist/plugins/zoom/index.js';

import GalleryZoomControls from './GalleryZoomControls';
import GalleryThumbnails from './GalleryThumbnails';
import GalleryIcon from './GalleryIcon';

import css from './ProductGallery.css';

const PLUGINS = [Counter, Zoom];

const GalleryLightbox = props => {
  const {
    slides,
    activeIndex,
    isOpen,
    onSelect,
    onClose,
    onExited,
    reducedMotion,
    title,
  } = props;
  const intl = useIntl();
  const zoomRef = useRef(null);

  return (
    <Lightbox
      className={css.lightbox}
      open={isOpen && slides.length > 0}
      close={onClose}
      index={activeIndex}
      slides={slides}
      plugins={PLUGINS}
      zoom={{ ref: zoomRef, zoomInMultiplier: 1.5, scrollToZoom: false }}
      carousel={{ padding: 0, spacing: 0, preload: 1, finite: slides.length <= 1 }}
      animation={{
        fade: reducedMotion ? 0 : 180,
        swipe: reducedMotion ? 0 : 250,
        zoom: reducedMotion ? 0 : 200,
      }}
      controller={{
        aria: true,
        closeOnPullUp: false,
        closeOnPullDown: false,
        closeOnEscape: true,
        closeOnBackdropClick: false,
      }}
      portal={{
        container: {
          'aria-label': intl.formatMessage({ id: 'ProductGallery.gallery' }),
          onKeyDownCapture: event => {
            if (event.ctrlKey || event.metaKey) {
              if (['+', '=', '-', '_', '0'].includes(event.key)) event.stopPropagation();
              return;
            }
            if (!event.altKey && zoomRef.current && (event.key === '0' || event.key === '=')) {
              event.preventDefault();
              event.stopPropagation();
              if (event.key === '0') zoomRef.current.changeZoom(1);
              else zoomRef.current.zoomIn();
            }
          },
        },
      }}
      labels={{
        Lightbox: intl.formatMessage({ id: 'ProductGallery.gallery' }),
        Close: intl.formatMessage({ id: 'ProductGallery.close' }),
        Previous: intl.formatMessage({ id: 'ProductGallery.previous' }),
        Next: intl.formatMessage({ id: 'ProductGallery.next' }),
        'Zoom in': intl.formatMessage({ id: 'ProductGallery.zoomIn' }),
        'Zoom out': intl.formatMessage({ id: 'ProductGallery.zoomOut' }),
        'Photo gallery': intl.formatMessage({ id: 'ProductGallery.gallery' }),
        '{index} of {total}': intl.formatMessage({ id: 'ProductGallery.photo' }, { index: '{index}', count: '{total}' }),
      }}
      on={{
        view: ({ index }) => {
          if (isOpen) onSelect(index);
        },
        exited: onExited,
      }}
      render={{
        iconPrev: () => <GalleryIcon name="left" />,
        iconNext: () => <GalleryIcon name="right" />,
        iconClose: () => <GalleryIcon name="close" />,
        buttonZoom: ({ zoom, minZoom, maxZoom, disabled }) => (
          <GalleryZoomControls
            percent={Math.round(zoom * 100)}
            canZoomIn={zoom < maxZoom - 0.001}
            canZoomOut={zoom > minZoom + 0.001}
            disabled={disabled}
            zoomRef={zoomRef}
          />
        ),
        controls: () =>
          slides.length > 0 ? (
            <GalleryThumbnails
              slides={slides}
              activeIndex={activeIndex}
              onSelect={onSelect}
              title={title}
              modal
              reducedMotion={reducedMotion}
            />
          ) : null,
        iconError: () => (
          <span className={css.imageError} role="status">
            {intl.formatMessage({ id: 'ProductGallery.error' })}
          </span>
        ),
        buttonPrev: slides.length <= 1 ? () => null : undefined,
        buttonNext: slides.length <= 1 ? () => null : undefined,
      }}
    />
  );
};

export default GalleryLightbox;
